import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';
import { toast } from 'sonner';
import { useLoginForm } from './useAuthForm';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  useLazyGetGoogleSignInLinkQuery,
  useGetUserQuery,
  useLoginMutation,
} from './auth.api.slice';
import { loginSchema } from './auth.schema';
import { LastUsedBadge } from './lastUsedProvider';
import { AuthProvider } from './auth.d';
import Google from '@/components/Icons/Google';
import {
  setShowForgetPasswordDialog,
  setShowLoginDialog,
} from '../global/global.slice';

function LoginForm() {
  const dispatch = useDispatch();
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [getGoogleSignInLink] = useLazyGetGoogleSignInLinkQuery();
  const { refetch: refetchUser } = useGetUserQuery();
  const loginForm = useLoginForm();

  async function handleLoginSubmit(values: z.infer<typeof loginSchema>) {
    try {
      const data = await login(values).unwrap();
      if (data.status !== 'success') {
        toast.error('Login Failed', {
          description: (data as any)?.errorMessage || 'Something went wrong',
          closeButton: true,
        });
      }
      await refetchUser();
      dispatch(setShowLoginDialog(false));
    } catch (error: any) {
      toast.error('Login Failed', {
        description: error?.data?.errorMessage || 'Something went wrong',
        closeButton: true,
      });
    }
  }

  const [isGoogleRedirecting, setIsGoogleRedirecting] = useState(false);

  async function handleGoogleLogin() {
    setIsGoogleRedirecting(true);
    try {
      const data = await getGoogleSignInLink().unwrap();
      window.location.href = data.url;
    } catch (error: any) {
      setIsGoogleRedirecting(false);
      toast.error('Could not start Google login', {
        description: error?.data?.errorMessage || 'Please try again.',
      });
    }
  }

  const handleOpenForgetPasswordModal = () => {
    dispatch(setShowLoginDialog(false));
    dispatch(setShowForgetPasswordDialog(true));
  };

  const busy = isLoginLoading || isGoogleRedirecting;

  return (
    <div className="flex flex-col gap-6">
      <DialogHeader className="sm:text-center">
        <DialogTitle>Log in</DialogTitle>
        <DialogDescription>
          Already have an account? Continue now!
        </DialogDescription>
      </DialogHeader>

      <Form {...loginForm}>
        <form
          onSubmit={loginForm.handleSubmit(handleLoginSubmit)}
          className="flex flex-col gap-4"
        >
          <FormField
            control={loginForm.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="username"
                    placeholder="Enter your email address"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={loginForm.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="relative">
            <Button type="submit" className="w-full" disabled={busy}>
              {isLoginLoading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Continue
            </Button>
            <LastUsedBadge provider={AuthProvider.Email} />
          </div>
        </form>
      </Form>

      <div className="relative">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-sm text-muted-foreground">
          Or
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <div className="relative">
          <Button
            onClick={handleGoogleLogin}
            className="w-full gap-2"
            variant="outline"
            type="button"
            disabled={busy}
          >
            {isGoogleRedirecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Redirecting to Google…
              </>
            ) : (
              <>
                <Google />
                Continue with Google
              </>
            )}
          </Button>
          <LastUsedBadge provider={AuthProvider.Google} />
        </div>
        <Button
          className="w-full"
          variant="link"
          type="button"
          onClick={handleOpenForgetPasswordModal}
        >
          Forget Password ?
        </Button>
      </div>
    </div>
  );
}

export default LoginForm;
